import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { Button, Grid, CircularProgress, Typography, Stack, Box } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import { RootState } from '../../../reducers';
import { gotoNextMenu, gotoPreviousMenu } from '../../../store/assessment.slice';
import { copyObject, highlightStyle } from '../../../utils/common';
import { saveEngagementDraft, setEngagementDraft } from '../../../store/documentEngagement.slice';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { SummaryNoteTypeEnum } from '../../../Enum/SummaryNoteTypeEnum';
import { SummaryNote } from '../../../Models/DocumentEngagements/SummaryNote.model';
import { DiagnosticType } from '../../../Enum/DiagnosticType';
import AlertDialog from '../../AlertDialog/AlertDialog';
import BHDiagnosticConfirmationBody from '../../Shared/BHDiagnosticConfirmationBody';
import { isAIIcon as IsAIIcon } from '../Assessment';
import { DocumentEngagement } from '../../../Models/DocumentEngagements/DocumentEngagement.model';
import { getProgressNoteConfirmation } from '../../../utils/summaryNoteConfirmation';

const classes = {
    title: {
        color: '#0063a7',
        marginTop: '10px',
    },
    richTextBox: {
        '& .mantine-RichTextEditor-content': {
            minHeight: '250px',
        },
    },
};

const ProgressNotes: React.FC<{ memberId: string }> = ({ memberId }) => {
    const dispatch = useAppDispatch();
    const { engagementDraft, memberAssessmentHistory, isSaving } = useSelector((state: RootState) => state.documentEngagement);
    const isBHDiagnostic = engagementDraft.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic;
    const [progressNotes, onChangeProgressNotes] = useState(engagementDraft.ProgressNotes || '');
    const [followupRecommendations, onChangeFollowupRecommendations] = useState(engagementDraft.FollowupRecommendations || '');

    const handleOnBlur = () => {
        let engagementDraftData = copyObject(engagementDraft);
        engagementDraftData.ProgressNotes = progressNotes;
        engagementDraftData.FollowupRecommendations = followupRecommendations;
        const updatedMemberAssessmentHistory = { ...memberAssessmentHistory, EngagementDraft: engagementDraftData };
        dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
    };

    const progressNotesEditor = useEditor({
        extensions: [StarterKit],
        content: progressNotes,
        onUpdate: ({ editor }) => {
            onChangeProgressNotes(editor.getHTML());
            if (editor.isFocused && engagementDraft.IsAIProgressNotes) {
                dispatch(setEngagementDraft({ ...engagementDraft, IsAIProgressNotes: false }));
            }
        },
        onBlur: handleOnBlur,
    });

    const followupEditor = useEditor({
        extensions: [StarterKit],
        content: followupRecommendations,
        onUpdate: ({ editor }) => {
            onChangeFollowupRecommendations(editor.getHTML());
            if (editor.isFocused && engagementDraft.IsAIFollowupRecommendations) {
                dispatch(setEngagementDraft({ ...engagementDraft, IsAIFollowupRecommendations: false }));
            }
        },
        onBlur: handleOnBlur,
    });

    useEffect(() => {
        if (progressNotesEditor && engagementDraft.ProgressNotes !== progressNotes) {
            progressNotesEditor.commands.setContent(engagementDraft.ProgressNotes || '');
        }
    }, [engagementDraft.ProgressNotes]);

    useEffect(() => {
        if (followupEditor && engagementDraft.FollowupRecommendations !== followupRecommendations) {
            followupEditor.commands.setContent(engagementDraft.FollowupRecommendations || '');
        }
    }, [engagementDraft.FollowupRecommendations]);

    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);

    const handleOnClickNext = () => {
        handleOnBlur();
        setShowSaveAlert(true);
    };
    const notesComplete = () => {
        if (!isBHDiagnostic) return true;
        return (
            progressNotes.trim() !== '<p><br></p>' &&
            progressNotes.trim() !== '' &&
            followupRecommendations.trim() !== '<p><br></p>' &&
            followupRecommendations.trim() !== ''
        );
    };

    const handleSubmit = (isComplete: boolean = false) => {
        let engagementDraftData = copyObject(engagementDraft);
        engagementDraftData.ProgressNoteStatus = isComplete ? StatusEnum.Completed : StatusEnum.None;
        engagementDraftData.SummaryNotes = prepareSummaryNotes(engagementDraftData);
        const updatedMemberAssessmentStatuses = {
            ...memberAssessmentHistory.MemberAssessmentStatuses,
            ProgressNoteStatus: StatusEnum.Completed,
        };
        const updatedMemberAssessmentHistory = {
            ...memberAssessmentHistory,
            EngagementDraft: engagementDraftData,
            MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
        };
        dispatch(saveEngagementDraft(updatedMemberAssessmentHistory, true));
    };

    const prepareSummaryNotes = (data: DocumentEngagement) => {
        let summaryNotesData = data.SummaryNotes.filter((s) => s.Type !== SummaryNoteTypeEnum.ProgressNote);
        if (Boolean(progressNotes)) {
            let summaryNotes = [];
            var domParser = new DOMParser();
            var htmlString = domParser.parseFromString(progressNotes, 'text/html');
            var paragraphHtmlTags = htmlString.getElementsByTagName('p');
            var paragraphList = Array.prototype.slice.call(paragraphHtmlTags);
            paragraphList.forEach((html) => {
                if (Boolean(html.innerHTML.length)) {
                    if (Boolean(html?.innerText)) {
                        summaryNotes.push(html.innerText);
                    }
                }
            });
            let notes = '';
            summaryNotes.forEach((note) => {
                if (!Boolean(notes)) notes = `<p>${note} </p>`;
                else notes += `<p>${note} </p>`;
            });
            if (notes) {
                const summaryNote = {
                    Id: '',
                    CptCode: '',
                    Zcode: '',
                    Note: notes,
                    Type: SummaryNoteTypeEnum.ProgressNote,
                    IsConfirm: getProgressNoteConfirmation(data.SummaryNotes),
                    OrderBy: 26,
                    DisplayName: 'Progress Note',
                } as SummaryNote;
                summaryNotesData.push(summaryNote);
            }
        }
        return summaryNotesData;
    };

    return (
        <Grid container direction="column" spacing={2} className="content-panel">
            <Grid className="content-form">
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="h6" sx={classes.title}>
                        Progress Notes {engagementDraft.IsAIProgressNotes && IsAIIcon()}
                    </Typography>
                    {isBHDiagnostic && (
                        <Typography variant="body2" component="label" ml={5} sx={highlightStyle}>
                            * Required for BH Diagnostic
                        </Typography>
                    )}
                </Stack>
                <Box sx={classes.richTextBox}>
                    <RichTextEditor editor={progressNotesEditor}>
                        <RichTextEditor.Toolbar>
                            <RichTextEditor.ControlsGroup>
                                <RichTextEditor.Bold />
                                <RichTextEditor.Italic />
                                <RichTextEditor.Underline />
                            </RichTextEditor.ControlsGroup>
                        </RichTextEditor.Toolbar>
                        <RichTextEditor.Content />
                    </RichTextEditor>
                </Box>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="h6" sx={classes.title}>
                        Follow Up / Recommendations {engagementDraft.IsAIFollowupRecommendations && IsAIIcon()}
                    </Typography>
                    {isBHDiagnostic && (
                        <Typography variant="body2" component="label" ml={5} sx={highlightStyle}>
                            * Required for BH Diagnostic
                        </Typography>
                    )}
                </Stack>
                <Box sx={classes.richTextBox}>
                    <RichTextEditor editor={followupEditor}>
                        <RichTextEditor.Toolbar>
                            <RichTextEditor.ControlsGroup>
                                <RichTextEditor.Bold />
                                <RichTextEditor.Italic />
                                <RichTextEditor.Underline />
                            </RichTextEditor.ControlsGroup>
                        </RichTextEditor.Toolbar>
                        <RichTextEditor.Content />
                    </RichTextEditor>
                </Box>
            </Grid>
            <Grid justifyContent="flex-end" className="content-footer">
                <Button
                    variant="contained"
                    color="secondary"
                    sx={{ mr: 3 }}
                    disabled={isSaving}
                    onClick={() => dispatch(gotoPreviousMenu())}
                    startIcon={<ArrowBackIosIcon />}
                >
                    Previous
                </Button>
                <Button
                    variant="contained"
                    endIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIosIcon />}
                    disabled={isSaving}
                    onClick={handleOnClickNext}
                >
                    Next
                </Button>
            </Grid>

            {Boolean(!notesComplete()) ? (
                <AlertDialog
                    open={showSaveAlert}
                    title="Progress Notes"
                    showBottomCenter={true}
                    cancelText="Go Back"
                    okText="Confirm"
                    onConfirm={() => handleSubmit(false)}
                    onClose={() => setShowSaveAlert(false)}
                    isLoading={isSaving}
                    renderBody={() =>
                        BHDiagnosticConfirmationBody({
                            assessmentName: 'Progress Notes',
                            diagnosticType: engagementDraft.DiagnosticStatus?.DiagnosticType ?? DiagnosticType.None,
                        })
                    }
                />
            ) : (
                <AlertDialog
                    open={showSaveAlert}
                    title="Progress Notes"
                    showBottomCenter={true}
                    cancelText="Go Back"
                    skipText="Skip and continue"
                    okText="Confirm"
                    onConfirm={() => handleSubmit(true)}
                    onSkip={() => dispatch(gotoNextMenu())}
                    onClose={() => setShowSaveAlert(false)}
                    isLoading={isSaving}
                    message="I confirm and acknowledge that Progress Notes are completed."
                />
            )}
        </Grid>
    );
};

export default ProgressNotes;
